/**
 * Sign-in.
 *
 * Two steps, mirroring the server: password, then second factor. The password
 * step returns no session at all — only a short-lived ticket — so this screen
 * cannot accidentally hold a half-authenticated user in a signed-in state.
 *
 * Two more screens hang off the password step, because a person who cannot
 * sign in has nowhere else to go (and this page is all an unauthenticated
 * visitor ever sees):
 *   forgot — ask for an emailed reset code
 *   reset  — trade that code for a new password. Invitations reuse this exact
 *            flow: an invited user's first password is "reset" from nothing.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useAuth, type Session } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { Button, Field, ErrorNotice, Spinner } from '../components/ui';
import { IconShield } from '../components/icons';
import {
  emailSchema, passwordSchema, PASSWORD_MIN, PASSWORD_MAX,
  LANGUAGES, LANGUAGE_NAMES, type MfaMethod, type Language,
} from '@opencomply/shared';
import { setLanguage } from '../i18n';

type Step = 'password' | 'mfa' | 'forgot' | 'reset';

const CODE_LENGTH = 6;

/**
 * The password rules, as the checklist shows them. Each id is what
 * `passwordSchema` reports when that rule fails, so the checklist is derived
 * from the real schema rather than a second copy of its regexes — if the
 * server-side rules change, this list cannot silently disagree with them.
 * (`length` is the one exception: zod reports it as too_small / too_big.)
 */
const PASSWORD_RULES = [
  { id: 'length', label: 'auth.pwRuleLength' },
  { id: 'PASSWORD_NEEDS_LOWERCASE', label: 'auth.pwRuleLower' },
  { id: 'PASSWORD_NEEDS_UPPERCASE', label: 'auth.pwRuleUpper' },
  { id: 'PASSWORD_NEEDS_DIGIT', label: 'auth.pwRuleDigit' },
  { id: 'PASSWORD_NEEDS_SYMBOL', label: 'auth.pwRuleSymbol' },
] as const;

/** Which rule ids the given password currently fails. */
function failedPasswordRules(password: string): Set<string> {
  const result = passwordSchema.safeParse(password);
  const failed = new Set<string>();
  if (result.success) return failed;
  for (const issue of result.error.issues) {
    failed.add(issue.code === 'too_small' || issue.code === 'too_big' ? 'length' : issue.message);
  }
  return failed;
}

/**
 * Read the deep link an invitation email carries: `?reset=1`, or a `code`
 * parameter, optionally with `email`. Pure (no URL rewriting here) because
 * React may run a state initialiser twice in development, and the second run
 * must see the same URL as the first.
 */
function readResetLink(): { open: boolean; email: string; code: string } {
  const params = new URLSearchParams(window.location.search);
  const rawCode = params.get('code');
  return {
    open: params.get('reset') === '1' || rawCode !== null,
    email: (params.get('email') ?? '').trim().slice(0, 254),
    code: (rawCode ?? '').replace(/\D/g, '').slice(0, CODE_LENGTH),
  };
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { setSession } = useAuth();
  const describeError = useApiError();

  // Captured once, on first render — see the effect below for why the URL is
  // then cleaned.
  const [link] = useState(readResetLink);

  const [step, setStep] = useState<Step>(link.open ? 'reset' : 'password');
  const [email, setEmail] = useState(link.email);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');
  // Only the two challenge kinds are ever shown here; 'NONE' never reaches
  // this state because that branch signs in without a code step.
  const [method, setMethod] = useState<'EMAIL_OTP' | 'TOTP'>('EMAIL_OTP');
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Forgot / reset. The reset code is kept apart from the MFA `code` so a
  // half-typed sign-in code can never leak into a password reset (or back).
  const [resetCode, setResetCode] = useState(link.code);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [requested, setRequested] = useState(false);
  // Client-side errors stay quiet until the first submit attempt, so a person
  // still typing is not shouted at.
  const [submitted, setSubmitted] = useState(false);

  const codeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step === 'mfa') codeRef.current?.focus();
    else if (step === 'forgot') emailRef.current?.focus();
    // Land on the first thing still missing. Deliberately keyed on `step`
    // only: re-running as the email is typed would steal focus mid-keystroke.
    else if (step === 'reset') (email ? codeRef : emailRef).current?.focus();
  }, [step]);

  // A reset code in the address bar ends up in browser history and can be
  // synced to other devices. It has been read into state above, so remove it
  // from the URL straight away. This also means a reload after finishing does
  // not reopen the reset screen.
  useEffect(() => {
    if (!link.open && !window.location.search) return;
    const params = new URLSearchParams(window.location.search);
    for (const key of ['reset', 'code', 'email']) params.delete(key);
    const rest = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
    );
  }, [link.open]);

  /** Move between screens without carrying stale errors or secrets along. */
  const goTo = (next: Step) => {
    setStep(next);
    setError(null);
    setNotice(null);
    setSubmitted(false);
    setRequested(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Two possible shapes. An account with a second factor returns a ticket
      // and we move to the code step; a password-only account (mfaMethod
      // 'NONE') is already signed in and the session comes back here.
      const result = await api.post<
        // The ticket branch is narrowed to the two kinds that actually produce
        // a challenge. 'NONE' can only appear on the signed-in branch, so the
        // code step never has to handle a method it cannot render.
        | { mfaRequired: true; ticket: string; method: Exclude<MfaMethod, 'NONE'> }
        | ({ mfaRequired: false; method: MfaMethod } & Session)
      >('/auth/login', { email, password });

      // The password is cleared from component state the moment it is no longer
      // needed. It should not sit in memory for the rest of the session.
      setPassword('');

      if (result.mfaRequired === false) {
        setSession(result);
        if (result.user.language && result.user.language !== i18n.language) {
          void i18n.changeLanguage(result.user.language);
        }
        return;
      }

      setTicket(result.ticket);
      setMethod(result.method);
      setStep('mfa');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api.post<Session>('/auth/mfa', {
        ticket,
        code: code.trim(),
        isBackupCode: useBackup,
      });
      setSession(session);
      if (session.user.language && session.user.language !== i18n.language) {
        void i18n.changeLanguage(session.user.language);
      }
    } catch (err) {
      setError(describeError(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setNotice(null);
    setError(null);
    try {
      await api.post('/auth/mfa/resend', { ticket });
      setNotice(t('auth.resent'));
    } catch (err) {
      setError(describeError(err));
    }
  };

  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!emailSchema.safeParse(email).success) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password/reset-request', { email: email.trim() });
      // The server answers identically whether or not the account exists, so
      // the confirmation is fixed text. Nothing here may depend on the
      // response, or the UI would undo that protection.
      setRequested(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const failedRules = failedPasswordRules(newPassword);
  const passwordValid = failedRules.size === 0;
  const passwordsMatch = newPassword === confirmPassword;
  // Mismatch is only flagged once the confirmation is as long as the password
  // (or after a submit attempt): before that it is merely a prefix, and
  // complaining while someone is still typing it is noise.
  const showMismatch =
    confirmPassword.length > 0 &&
    !passwordsMatch &&
    (submitted || confirmPassword.length >= newPassword.length);
  const emailOk = emailSchema.safeParse(email).success;
  const codeOk = resetCode.length === CODE_LENGTH;

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    // Checked here as well as shown live: this is what stops a doomed request
    // (and a spent attempt against the reset rate limit) on a typo.
    if (!emailOk || !codeOk || !passwordValid || !passwordsMatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password/reset-confirm', {
        email: email.trim(),
        code: resetCode,
        newPassword,
      });
      // Do NOT sign the user in. Sending them back to the sign-in step makes
      // them type the new password once, which proves it was saved as they
      // think it was. The email stays filled so that is one field, not two.
      goTo('password');
      setResetCode('');
      setNotice(t('auth.resetDone'));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'AUTH_RESET_INVALID') {
        // Same answer for an unknown email and a bad code, on purpose — so we
        // say the same thing, and only offer the fix that always applies.
        setError(t('auth.resetInvalid'));
        setResetCode('');
        codeRef.current?.focus();
      } else {
        if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') setSubmitted(true);
        setError(describeError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2 bg-surface-sunken">
      {/* Brand panel — desktop only. On a phone this space belongs to the form. */}
      <div className="hidden lg:flex flex-col justify-between bg-brand text-white p-12">
        <div className="flex items-center gap-3">
          <IconShield className="w-9 h-9" />
          <span className="text-xl font-semibold">{t('app.name')}</span>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl font-semibold leading-tight">
            Compliance you can evidence.
          </h1>
          <p className="mt-4 text-white/80 leading-relaxed">
            Assess against NIS2, DORA and ISO&nbsp;27001:2022, turn findings into tracked
            remediation, and keep an audit trail that holds up under scrutiny.
          </p>
        </div>
        <p className="text-sm text-white/60">{t('app.tagline')}</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <IconShield className="w-8 h-8 text-brand" />
            <span className="text-lg font-semibold text-ink">{t('app.name')}</span>
          </div>

          {step === 'password' ? (
            <form onSubmit={submitPassword} noValidate>
              <h1 className="oc-h1 mb-1">{t('auth.welcome')}</h1>
              <p className="oc-muted mb-6">{t('auth.subtitle')}</p>

              {error && <div className="mb-4"><ErrorNotice message={error} /></div>}
              {/* Shown after a successful password reset. */}
              {notice && (
                <p className="mb-4 text-sm text-good" role="status">{notice}</p>
              )}

              <Field label={t('auth.email')} htmlFor="email" required>
                <input
                  id="email"
                  type="email"
                  className="oc-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  inputMode="email"
                  required
                />
              </Field>

              <Field label={t('auth.password')} htmlFor="password" required>
                <input
                  id="password"
                  type="password"
                  className="oc-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>

              <Button type="submit" variant="primary" className="w-full mt-2" disabled={busy}>
                {busy ? <Spinner className="w-4 h-4 text-white" /> : t('auth.continue')}
              </Button>

              {/* min-h keeps these at a 44px touch target. "I have a code" is
                  here because an invitation email says only "open the app" —
                  someone arriving without the deep link needs a way in. */}
              <div className="mt-2 flex flex-col text-sm text-center">
                <button
                  type="button"
                  onClick={() => goTo('forgot')}
                  className="min-h-[2.75rem] text-brand hover:underline"
                >
                  {t('auth.forgotPassword')}
                </button>
                <button
                  type="button"
                  onClick={() => goTo('reset')}
                  className="min-h-[2.75rem] text-ink-muted hover:text-ink-secondary"
                >
                  {t('auth.haveCode')}
                </button>
              </div>
            </form>
          ) : step === 'mfa' ? (
            <form onSubmit={submitCode} noValidate>
              <h1 className="oc-h1 mb-1">{t('auth.mfaTitle')}</h1>
              <p className="oc-muted mb-6">
                {useBackup
                  ? t('auth.backupCode')
                  : method === 'TOTP'
                    ? t('auth.mfaTotpHint')
                    : t('auth.mfaEmailHint')}
              </p>

              {error && <div className="mb-4"><ErrorNotice message={error} /></div>}
              {notice && (
                <p className="mb-4 text-sm text-good" role="status">{notice}</p>
              )}

              <Field label={useBackup ? t('auth.backupCode') : t('auth.code')} htmlFor="code" required>
                <input
                  ref={codeRef}
                  id="code"
                  type="text"
                  className="oc-input text-center tracking-[0.4em] font-mono !text-lg"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
                  // `one-time-code` lets iOS and Android offer the SMS/email code
                  // straight from the keyboard.
                  autoComplete="one-time-code"
                  inputMode={useBackup ? 'text' : 'numeric'}
                  maxLength={useBackup ? 10 : 6}
                  required
                />
              </Field>

              <Button type="submit" variant="primary" className="w-full" disabled={busy}>
                {busy ? <Spinner className="w-4 h-4 text-white" /> : t('auth.verify')}
              </Button>

              <div className="mt-4 flex flex-col gap-2 text-sm text-center">
                {method === 'EMAIL_OTP' && !useBackup && (
                  <button type="button" onClick={() => void resend()} className="text-brand hover:underline">
                    {t('auth.resend')}
                  </button>
                )}
                {method === 'TOTP' && (
                  <button
                    type="button"
                    onClick={() => { setUseBackup(!useBackup); setCode(''); }}
                    className="text-brand hover:underline"
                  >
                    {useBackup ? t('auth.useAppCode') : t('auth.useBackupCode')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setStep('password'); setCode(''); setError(null); }}
                  className="text-ink-muted hover:text-ink-secondary"
                >
                  {t('auth.back')}
                </button>
              </div>
            </form>
          ) : step === 'forgot' ? (
            requested ? (
              <div>
                <h1 className="oc-h1 mb-1">{t('auth.forgotTitle')}</h1>
                {/* Fixed wording, shown whatever the server knows about this
                    address — see submitForgot. */}
                <p className="oc-muted mb-6" role="status">{t('auth.resetRequested')}</p>
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => goTo('reset')}
                >
                  {t('auth.haveCode')}
                </Button>
                <div className="mt-2 flex flex-col text-sm text-center">
                  <button
                    type="button"
                    onClick={() => goTo('password')}
                    className="min-h-[2.75rem] text-ink-muted hover:text-ink-secondary"
                  >
                    {t('auth.backToSignIn')}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitForgot} noValidate>
                <h1 className="oc-h1 mb-1">{t('auth.forgotTitle')}</h1>
                <p className="oc-muted mb-6">{t('auth.forgotSubtitle')}</p>

                {error && <div className="mb-4"><ErrorNotice message={error} /></div>}

                <Field
                  label={t('auth.email')}
                  htmlFor="forgot-email"
                  required
                  error={submitted && !emailOk ? t('auth.emailInvalid') : undefined}
                >
                  <input
                    ref={emailRef}
                    id="forgot-email"
                    type="email"
                    className="oc-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    inputMode="email"
                    required
                  />
                </Field>

                <Button type="submit" variant="primary" className="w-full mt-2" disabled={busy}>
                  {busy ? <Spinner className="w-4 h-4 text-white" /> : t('auth.sendCode')}
                </Button>

                <div className="mt-2 flex flex-col text-sm text-center">
                  <button
                    type="button"
                    onClick={() => goTo('password')}
                    className="min-h-[2.75rem] text-ink-muted hover:text-ink-secondary"
                  >
                    {t('auth.backToSignIn')}
                  </button>
                </div>
              </form>
            )
          ) : (
            <form onSubmit={submitReset} noValidate>
              <h1 className="oc-h1 mb-1">{t('auth.resetTitle')}</h1>
              <p className="oc-muted mb-6">{t('auth.resetSubtitle')}</p>

              {error && <div className="mb-4"><ErrorNotice message={error} /></div>}

              <Field
                label={t('auth.email')}
                htmlFor="reset-email"
                required
                error={submitted && !emailOk ? t('auth.emailInvalid') : undefined}
              >
                <input
                  ref={emailRef}
                  id="reset-email"
                  type="email"
                  className="oc-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  inputMode="email"
                  required
                />
              </Field>

              <Field
                label={t('auth.code')}
                htmlFor="reset-code"
                required
                // Not while a server error is up: after a rejected code the field is
                // cleared on purpose, and two messages saying the same thing is noise.
                error={submitted && !codeOk && !error ? t('auth.codeInvalid') : undefined}
              >
                <input
                  ref={codeRef}
                  id="reset-code"
                  type="text"
                  className="oc-input text-center tracking-[0.4em] font-mono"
                  value={resetCode}
                  // Digits only: the code is copied out of an email, where it
                  // often picks up spaces or a trailing newline. There is no
                  // maxLength attribute on purpose — the browser would truncate
                  // a pasted "123 456" to "123 45" before this handler could
                  // strip the space. The slice below enforces the length.
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  required
                />
              </Field>

              <Field label={t('auth.newPassword')} htmlFor="new-password" required>
                <input
                  id="new-password"
                  type="password"
                  className="oc-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  aria-describedby="password-rules"
                  required
                />
              </Field>

              {/* Live checklist, driven by the real schema. Met/unmet is
                  spelled out in text as well as colour and glyph, so it does
                  not depend on seeing red versus green. A rule that is still
                  unmet turns red only after a submit attempt — until then it
                  is simply "not yet". */}
              <ul id="password-rules" className="-mt-2 mb-4 space-y-1 text-xs" aria-label={t('auth.pwRulesTitle')}>
                {PASSWORD_RULES.map((rule) => {
                  const met = newPassword.length > 0 && !failedRules.has(rule.id);
                  return (
                    <li
                      key={rule.id}
                      className={
                        met ? 'text-good' : submitted ? 'text-critical' : 'text-ink-muted'
                      }
                    >
                      <span aria-hidden>{met ? '\u2713' : '\u25CB'}</span>{' '}
                      {t(rule.label, { min: PASSWORD_MIN, max: PASSWORD_MAX })}
                      <span className="sr-only">
                        {' '}({met ? t('auth.pwRuleMet') : t('auth.pwRuleUnmet')})
                      </span>
                    </li>
                  );
                })}
              </ul>

              <Field
                label={t('auth.confirmPassword')}
                htmlFor="confirm-password"
                required
                error={showMismatch ? t('auth.passwordMismatch') : undefined}
              >
                <input
                  id="confirm-password"
                  type="password"
                  className="oc-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>

              <Button type="submit" variant="primary" className="w-full mt-2" disabled={busy}>
                {busy ? <Spinner className="w-4 h-4 text-white" /> : t('auth.setPassword')}
              </Button>

              <div className="mt-2 flex flex-col text-sm text-center">
                {/* Codes expire (an invitation's lasts days, a reset's
                    minutes), so an expired one must lead somewhere. */}
                <button
                  type="button"
                  onClick={() => goTo('forgot')}
                  className="min-h-[2.75rem] text-brand hover:underline"
                >
                  {t('auth.needNewCode')}
                </button>
                <button
                  type="button"
                  onClick={() => goTo('password')}
                  className="min-h-[2.75rem] text-ink-muted hover:text-ink-secondary"
                >
                  {t('auth.backToSignIn')}
                </button>
              </div>
            </form>
          )}

          {/* Language switcher, available before sign-in — a user who cannot
              read the form cannot sign in to change their preference later.

              EVERY OPTION IS WRITTEN IN ITS OWN LANGUAGE (Deutsch, Ελληνικά,
              Čeština — never "German, Greek, Czech"), which is why it reads
              from LANGUAGE_NAMES rather than from the translation bundles.
              Someone hunting for their language does not read English to find
              it, and this control exists precisely for the person who is
              currently looking at an interface they cannot read.

              A <select> rather than a row of pills: ten of those wrap into a
              block of noise under the sign-in form, and a native select is
              also the control a screen reader and a phone keyboard handle
              best. */}
          <div className="mt-8 flex justify-center">
            <label className="sr-only" htmlFor="login-language">{t('common.language')}</label>
            <select
              id="login-language"
              className="oc-input !w-auto text-xs text-ink-muted"
              value={i18n.language}
              onChange={(e) => setLanguage(e.target.value as Language)}
            >
              {LANGUAGES.map((lng) => (
                <option key={lng} value={lng}>{LANGUAGE_NAMES[lng]}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
